/**
 * Service category configuration (S037; REQ-BM-014/015, REQ-DAT-004, REQ-CAT-013, REQ-CAT-043).
 *
 * A service category (a car "go", a "moto", a "send" courier lane) is **configuration**, never a
 * hardcoded enum: the same engine activates any set of categories per market from data (REQ-BM-014 — do
 * not hardcode categories; REQ-CAT-043 — categories are activated/configured per operational city).
 * The single driver identity switches between categories/modalities without a separate app (REQ-BM-015);
 * that is a property of category eligibility being data, evaluated by one engine.
 *
 * A `ServiceCategory` is **versioned + effective-dated + market-scoped** (REQ-DAT-004 is the durable
 * `service_categories` row). A category change is a new version with its own effective window; a ride
 * that snapshotted an earlier version keeps that version's config, so a change never mutates old rides
 * (see `snapshotCategory` in ./registry). Neutral: money is minor units + ISO currency, no country or
 * provider concept leaks into a category; category codes and vehicle classes are opaque tokens.
 *
 * Aggregate separation (ADR-0002 / C-01): a category declares its `kind`. A `delivery` category's order
 * lifecycle is NOT the passenger Ride state machine (REQ-CMR-022) even though it may share dispatch
 * infrastructure (REQ-CMR-023); `kind` is how downstream keeps the two aggregates apart.
 */
// ---- Neutral vocabularies ------------------------------------------------------
/** Which aggregate a category drives. Keeps passenger Ride separate from a delivery corrida (C-01). */
export const AGGREGATE_KINDS = ['mobility', 'delivery'];
/**
 * A physical vehicle class a category may use — a physical capability, never a service-category or
 * country enum (AGENTS packages rule; mirrors the S018 vehicle `type` distinction).
 */
export const VEHICLE_CLASSES = ['bike', 'moto', 'car', 'van', 'utility', 'truck'];
/** All capabilities default off; a partial config sets only what a category enables. */
export function categoryCapabilities(partial) {
    return {
        scheduling: partial?.scheduling ?? false,
        negotiation: partial?.negotiation ?? false,
        dynamicPricing: partial?.dynamicPricing ?? false,
        pet: partial?.pet ?? false,
        extraBaggage: partial?.extraBaggage ?? false,
        accessibility: partial?.accessibility ?? false,
        matchingPriority: partial?.matchingPriority ?? false,
        thirdPartyRide: partial?.thirdPartyRide ?? false,
        proofOfDelivery: partial?.proofOfDelivery ?? false,
    };
}
const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
/**
 * Validate a category configuration into an immutable `ServiceCategory` or a list of stable reason codes.
 * A programmer never gets a half-valid category: every field that a downstream decision depends on is
 * checked here, once.
 */
export function defineServiceCategory(input) {
    const reasons = [];
    if (input.code.trim() === '')
        reasons.push('EMPTY_CODE');
    if (input.displayName.trim() === '')
        reasons.push('EMPTY_DISPLAY_NAME');
    if (!Number.isInteger(input.version) || input.version < 1)
        reasons.push('INVALID_VERSION');
    if (!AGGREGATE_KINDS.includes(input.kind))
        reasons.push('INVALID_KIND');
    if (!UTC_INSTANT.test(input.effectiveFrom))
        reasons.push('INVALID_EFFECTIVE_FROM');
    if (input.effectiveTo !== undefined) {
        if (!UTC_INSTANT.test(input.effectiveTo) || Date.parse(input.effectiveTo) <= Date.parse(input.effectiveFrom)) {
            reasons.push('INVALID_EFFECTIVE_WINDOW');
        }
    }
    if (input.vehicleClasses.length === 0)
        reasons.push('NO_VEHICLE_CLASS');
    else if (input.vehicleClasses.some((v) => !VEHICLE_CLASSES.includes(v)))
        reasons.push('UNKNOWN_VEHICLE_CLASS');
    if (input.dispatchStrategy.trim() === '')
        reasons.push('EMPTY_DISPATCH_STRATEGY');
    if (input.pricingProfileId.trim() === '')
        reasons.push('EMPTY_PRICING_PROFILE');
    if (input.payment.acceptedInstruments.length === 0)
        reasons.push('NO_PAYMENT_INSTRUMENT');
    if (input.kind === 'mobility' && input.declaredItemConstraints !== undefined) {
        reasons.push('DECLARED_ITEMS_ON_MOBILITY');
    }
    const c = input.declaredItemConstraints;
    if (c !== undefined &&
        ([c.maxWeightGrams, c.maxLongestDimensionMm].some((n) => n !== undefined && (!Number.isFinite(n) || n < 0)) ||
            (c.maxDeclaredValue !== undefined && c.maxDeclaredValue.amountMinor < 0n))) {
        reasons.push('NEGATIVE_CONSTRAINT');
    }
    if (reasons.length > 0)
        return { ok: false, reasons };
    return { ok: true, value: input };
}
//# sourceMappingURL=category.js.map